import { NavigationBack, Typography } from "@/components";
import { cn } from "@/libraries/cn";
import { fromYearToAge } from "@/libraries/date";
import { MESSAGE_TAG } from "@/schemas";
import type { GetCaseResponse } from "@/types";
import { ChevronRightRounded } from "@mui/icons-material";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Toolbar from "@mui/material/Toolbar";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

interface AppBarCaseChatProps {
	caseItem: GetCaseResponse;
	tabs: Record<"label" | "value", string>[];
	activeTab: string;
	activeSubTab?: string;
	onChangeTab: (value: string) => void;
	onClickSearch: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onChangeSubTab?: (
		event: React.MouseEvent<HTMLElement>,
		value: string | null,
	) => void;
}

const AppBarCaseChat = (props: AppBarCaseChatProps) => {
	const {
		caseItem,
		tabs,
		activeTab = "all",
		activeSubTab = "all",
		onChangeTab,
		onClickSearch,
		onChangeSubTab,
	} = props;

	const tabValue = tabs.findIndex((tab) => tab.value === activeTab);

	const title = useMemo(() => {
		if (!caseItem.patient) return caseItem.title;
		return [caseItem.patient.name, caseItem.patient.surname].join(" ");
	}, [caseItem]);

	const age = useMemo(() => {
		if (!caseItem.patient?.dob)
			return fromYearToAge(caseItem.year_of_birth, " yo");
		return fromYearToAge(caseItem.patient.dob, " yo");
	}, [caseItem]);

	const subtitle = useMemo(() => {
		if (!caseItem.patient) return caseItem.summary;
		return caseItem.title;
	}, [caseItem]);

	return (
		<>
			<AppBar
				position="relative"
				color="transparent"
				className="!bg-gradient-account [&_*_]:text-white"
			>
				<Toolbar className="md:!container !gap-4" sx={{ p: 0 }}>
					<NavigationBack />
					<Link
						to="/dashboard/case/view/$id/detail"
						params={{ id: caseItem.id }}
						className="flex flex-col justify-center items-start flex-1"
					>
						<Typography fontSize={14} fontWeight={700}>
							{title}
							<ChevronRightRounded />
						</Typography>
						<Typography
							fontSize={12}
							fontWeight={400}
							className="flex justify-center items-center gap-2"
						>
							<Typography
								component="span"
								fontSize={12}
								fontWeight={400}
								className="flex items-end gap-1"
							>
								<PersonRoundedIcon fontSize="small" />
								{age}
							</Typography>
							<Typography
								component="span"
								fontSize={12}
								fontWeight={400}
								className="flex items-end gap-1"
							>
								<FolderRoundedIcon fontSize="small" />
								{subtitle}
							</Typography>
						</Typography>
					</Link>
					<div>
						{caseItem.status !== "closed" ? (
							<Link
								to="/dashboard/case/view/$id/members"
								params={{ id: caseItem.id }}
							>
								<IconButton color="primary">
									<PersonAddAltRoundedIcon className="text-default " />
								</IconButton>
							</Link>
						) : null}
						<Link
							to="/dashboard/case/view/$id/detail"
							params={{ id: caseItem.id }}
							search={{ tab: "medical_notes" }}
						>
							<IconButton color="primary">
								<AssignmentRoundedIcon className="text-default " />
							</IconButton>
						</Link>
						<IconButton color="primary" onClick={onClickSearch}>
							<SearchOutlinedIcon className="text-default" />
						</IconButton>
					</div>
				</Toolbar>
				<Toolbar
					className="[&_.MuiTabs-flexContainer]:md:justify-center [&_.MuiTabs-flexContainer]:justify-normal"
					classes={{
						root: "!p-0 !items-end",
					}}
				>
					{tabs.length ? (
						<Tabs
							value={tabValue}
							onChange={(_, val) => onChangeTab(tabs[val].value)}
							variant="scrollable"
							indicatorColor="primary"
							textColor="inherit"
							scrollButtons="auto"
							aria-label="scrollable auto tabs example chat"
							className="w-full [&_span.MuiTabs-indicator]:!bg-white"
						>
							{tabs.map((tab) => (
								<Tab
									key={tab.value}
									label={tab.label}
									sx={{ textTransform: "none" }}
								/>
							))}
						</Tabs>
					) : null}
				</Toolbar>
			</AppBar>
			{activeTab === MESSAGE_TAG.FILES && onChangeSubTab ? (
				<div className="container py-3 flex flex-row justify-center items-center">
					<ToggleButtonGroup
						value={activeSubTab}
						exclusive
						aria-label="Team Scope"
						size="small"
						className="gap-2"
						onChange={onChangeSubTab}
					>
						<ToggleButton
							key="all"
							value="all"
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								All
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.RESULTS}
							value={MESSAGE_TAG.RESULTS}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								Result
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.IMAGING}
							value={MESSAGE_TAG.IMAGING}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								Imaging
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.PHOTOS}
							value={MESSAGE_TAG.PHOTOS}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								Picture
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.OTHER}
							value={MESSAGE_TAG.OTHER}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								Other
							</Typography>
						</ToggleButton>
					</ToggleButtonGroup>
				</div>
			) : null}
		</>
	);
};

export default AppBarCaseChat;
